import { messageToClientSchema, type MessageToClient } from "@engine/network";
import { type ZodError } from "zod";

export type ServerMessageProcessorOptions = {
  /** Apply a successfully parsed message (the existing client.handleMessage). */
  handleMessage: (message: MessageToClient) => Promise<void>;
  /**
   * Called exactly once, on the first message this client's schema cannot
   * parse. The caller is expected to stop the socket and surface the
   * "refresh / update the app" notice.
   */
  onSchemaSkew: (error: ZodError, unparsedMessage: unknown) => void;
};

export type UnparsedServerMessageSummary = {
  messageType: string | null;
  actionCount: number | null;
  payloadTypes: string[];
};

/**
 * Wraps the action-sync socket's message handling with schema-skew
 * protection. A message this build's schema cannot parse means the server
 * (or another client) is running a newer action schema — retrying can never
 * succeed, and the server redelivers unacknowledged batches forever, so one
 * skewed message would otherwise loop an error every few seconds for the
 * rest of the session. Instead: report skew once, and drop every subsequent
 * message — including parseable ones, since a reducer behind the server's
 * schema could corrupt local state by applying a partial view of the log.
 *
 * Errors thrown by handleMessage itself (apply failures on a message that
 * parsed fine) are NOT skew and propagate to the caller unchanged.
 */
export function createServerMessageProcessor(
  options: ServerMessageProcessorOptions,
): (unparsedMessage: unknown) => Promise<void> {
  let skewDetected = false;

  return async (unparsedMessage: unknown) => {
    if (skewDetected) {
      return;
    }

    const parsed = messageToClientSchema.safeParse(unparsedMessage);

    if (!parsed.success) {
      skewDetected = true;
      options.onSchemaSkew(parsed.error, unparsedMessage);
      return;
    }

    await options.handleMessage(parsed.data);
  };
}

/**
 * Best-effort shape summary of a message that failed to parse, for error
 * reporting: which payload types the batch carried (so the unknown type is
 * visible in Sentry) without attaching payload contents.
 */
export function summarizeUnparsedServerMessage(
  unparsedMessage: unknown,
): UnparsedServerMessageSummary {
  const message =
    typeof unparsedMessage === "object" && unparsedMessage !== null
      ? (unparsedMessage as Record<string, unknown>)
      : null;
  const actions = Array.isArray(message?.actions) ? message.actions : null;
  const payloadTypes = new Set<string>();

  for (const action of actions ?? []) {
    if (typeof action !== "object" || action === null) {
      continue;
    }

    const payload = (action as Record<string, unknown>).payload;

    if (typeof payload !== "object" || payload === null) {
      continue;
    }

    const type = (payload as Record<string, unknown>).type;

    if (typeof type === "string") {
      payloadTypes.add(type);
    }
  }

  return {
    messageType: typeof message?.type === "string" ? message.type : null,
    actionCount: actions ? actions.length : null,
    payloadTypes: [...payloadTypes],
  };
}
